import {
  type FollowerAbilityId,
  type FollowerModifiers,
  type FollowerSkill,
  type FollowerTemplate,
  type FollowerTrait,
  type PeculiarityEffect,
} from '../schemas';

/**
 * **FI-8b — D1.5**: orchestrazione delle peculiarità eterogenee del
 * Follower (caso: Famiglio del Mago).
 *
 * Modulo dedicato per rispettare `max-lines: 400` su `followerCreation.ts`,
 * dove vivono già gli helper FI-8a (modifierBonus, archetype, traits,
 * name). Le peculiarità del Famiglio aggiungono ~200 righe di dispatch
 * eterogeneo (5 `kind` di effect, gestione `allowDuplicate`).
 *
 * API pubblica: {@link applyPeculiarities} per il flow di costruzione,
 * {@link mergeSkills} per fondere `fixedSkills` con gli skill prodotti
 * dalle peculiarità, {@link isPeculiarityEffectComplete} per il predicate
 * di setup-complete.
 */

export const FOLLOWER_ABILITY_IDS: readonly FollowerAbilityId[] = [
  'adepto',
  'animista',
  'battitore',
  'combattente',
  'guardia-del-corpo',
  'menestrello',
  'sacerdote',
  'scassinatore',
  'scout',
];

export const STAT_IDS_TO_MOD_KEY: Record<string, keyof FollowerModifiers> = {
  forza: 'forza',
  destrezza: 'destrezza',
  costituzione: 'costituzione',
  intelligenza: 'intelligenza',
  saggezza: 'saggezza',
  carisma: 'carisma',
};

export interface PeculiarityInput {
  selectedChoiceGroups: Readonly<Record<string, readonly string[]>>;
  narrativeFieldValues: Readonly<Record<string, string>>;
}

export interface PeculiarityResult {
  skillAdditions: FollowerSkill[];
  traitAdditions: FollowerTrait[];
  modifiers: FollowerModifiers;
  /**
   * **D8a** — bonus PF scalante al livello del PG, accumulato dalle
   * peculiarità di `kind: 'hp-bonus-scaling'`. Consumato da
   * `computeFollowerMaxHp` in `buildFollowerFromTemplate`. Assente se
   * nessuna peculiarità del kind è stata selezionata.
   */
  hpBonusScaling?: { perCharacterLevel: number; max: number };
  /**
   * **D8a** — flag impostato a `true` se almeno una peculiarità di
   * `kind: 'cost-suspension'` è stata selezionata. Consumato da
   * `buildFollowerFromTemplate` per impostare `Follower.cost.suspended`.
   */
  costSuspended: boolean;
}

/**
 * Accumulatore mutabile dei delta prodotti dal dispatch di `PeculiarityEffect`.
 * Esposto come API pubblica (D8c — FI-41) per consentire al service
 * `followerUpgrades.ts` di riusare il dispatcher dei 7 `kind` sui effetti
 * choice-based di una mossa di upgrade. Pattern «paga-una-volta-sola»: il
 * dispatch dei kind vive in un unico posto.
 */
export interface PeculiarityDispatchAcc {
  modifiers: FollowerModifiers;
  skillAdditions: FollowerSkill[];
  traitAdditions: FollowerTrait[];
  hpBonusScaling?: { perCharacterLevel: number; max: number };
  costSuspended: boolean;
}

/**
 * Contesto immutabile per un dispatch di `PeculiarityEffect`. Esposto come
 * API pubblica (D8c — FI-41) per consentire al service `followerUpgrades.ts`
 * di costruire un context equivalente leggendo dal `MoveRef` della mossa di
 * upgrade (anziché dal MoveRef della mossa-base).
 */
export interface PeculiarityDispatchContext {
  template: FollowerTemplate;
  input: PeculiarityInput;
  optionId: string;
  count: number;
}

/**
 * Itera le opzioni selezionate dal giocatore nel `ChoiceGroup` indicato
 * dal `template.peculiaritiesFromChoiceGroup`, raccoglie le occorrenze
 * (la stessa opzione può essere selezionata 2× per
 * `MoveChoice.allowDuplicate: true`) e dispatch sul `kind` di ciascun
 * `PeculiarityEffect`.
 *
 * Regola cumulativa (manuale V1, r. 4192-4198):
 *  - `skill-rank-cumulative` / `skill-rank-from-narrative`: rango finale =
 *    `min(3, baseRank + (count - 1))` (cap a R3 dal vincolo schema).
 *  - `modifier-from-narrative`: amount finale = `baseAmount + (count - 1)`
 *    (cap a +3 sulla stessa stat — `StatModifierSchema` enforce).
 *  - `narrative-trait`: 1 trait fisso, sempre (UI impedisce duplicato a
 *    monte; se duplicato l'effetto è identico — trait aggiunto 1 volta).
 *  - `custom-trait-from-narrative`: 1 trait per occorrenza, indipendente.
 *    Per la 2ª usa i field `*Second` definiti nell'effect.
 */
export function applyPeculiarities(
  template: FollowerTemplate,
  input: PeculiarityInput,
  baseModifiers: FollowerModifiers,
): PeculiarityResult {
  const acc: PeculiarityDispatchAcc = {
    modifiers: { ...baseModifiers },
    skillAdditions: [],
    traitAdditions: [],
    costSuspended: false,
  };

  const mapping = template.peculiaritiesFromChoiceGroup;
  if (!mapping) return acc;

  const selected = input.selectedChoiceGroups[mapping.fromChoiceGroup] ?? [];
  if (selected.length === 0) return acc;

  const counts = countOccurrences(selected);
  for (const [optionId, count] of counts) {
    const effect = mapping.optionToEffect[optionId];
    if (!effect) {
      throw new Error(
        `Template '${template.id}': nessuna peculiarità mappata per opzione '${optionId}'`,
      );
    }
    applyPeculiarityEffect(effect, { template, input, optionId, count }, acc);
  }

  return acc;
}

/**
 * Fonde `fixedSkills` del template con le `skillAdditions` prodotte dalle
 * peculiarità. Per ogni `abilityId` collidente, il rango finale è il max
 * dei due e `isPrimary` è OR. Conserva `movesUsedThisCamp/Session` della
 * fixedSkill quando esiste.
 */
export function mergeSkills(
  fixed: readonly FollowerSkill[],
  additions: readonly FollowerSkill[],
): FollowerSkill[] {
  const merged = new Map<FollowerAbilityId, FollowerSkill>();
  for (const s of fixed) merged.set(s.abilityId, s);
  for (const s of additions) {
    const existing = merged.get(s.abilityId);
    if (existing) {
      merged.set(s.abilityId, {
        ...existing,
        rank: Math.max(existing.rank, s.rank) as 1 | 2 | 3,
        isPrimary: existing.isPrimary || s.isPrimary,
      });
    } else {
      merged.set(s.abilityId, s);
    }
  }
  return Array.from(merged.values());
}

/**
 * Predicate: questo `PeculiarityEffect` ha tutti i prompt necessari
 * compilati? Usato da `isFollowerSetupComplete` di `followerCreation.ts`
 * per filtrare i Follower in costruzione.
 */
export function isPeculiarityEffectComplete(
  effect: PeculiarityEffect,
  count: number,
  fields: Readonly<Record<string, string>>,
): boolean {
  switch (effect.kind) {
    case 'skill-rank-cumulative':
    case 'narrative-trait':
    case 'hp-bonus-scaling':
    case 'cost-suspension':
      // **D8a**: i 2 nuovi kind D8a non hanno prompt aggiuntivi — sempre completi.
      return true;
    case 'skill-rank-from-narrative':
    case 'modifier-from-narrative':
      return isFieldFilled(fields, effect.narrativeFieldId);
    case 'custom-trait-from-narrative':
      if (!isFieldFilled(fields, effect.labelFieldId)) return false;
      if (count >= 2) {
        if (!effect.labelFieldIdSecond) return false;
        if (!isFieldFilled(fields, effect.labelFieldIdSecond)) return false;
      }
      return true;
  }
}

export function countOccurrences(selected: readonly string[]): Map<string, number> {
  const counts = new Map<string, number>();
  for (const id of selected) {
    counts.set(id, (counts.get(id) ?? 0) + 1);
  }
  return counts;
}

// -- Internals --

/**
 * Dispatcher dei 7 `PeculiarityEffect.kind` (FI-8b + D8a). Esposto come API
 * pubblica (D8c — FI-41) per consentire al service `followerUpgrades.ts` di
 * applicare gli effetti choice-based di una mossa di upgrade al Follower
 * esistente. Mutazione di `acc` (skillAdditions / traitAdditions / modifiers
 * / hpBonusScaling / costSuspended).
 *
 * Le 5 funzioni helper interne (`readAbilityIdFromNarrative`,
 * `readStatIdFromNarrative`, `cumulativeRank`, `buildCustomTraitInstances`,
 * `readCustomTrait`, `readField`) restano private — il consumer di FI-41
 * passa attraverso `applyPeculiarityEffect`.
 */
export function applyPeculiarityEffect(
  effect: PeculiarityEffect,
  ctx: PeculiarityDispatchContext,
  acc: PeculiarityDispatchAcc,
): void {
  switch (effect.kind) {
    case 'skill-rank-cumulative':
      acc.skillAdditions.push({
        abilityId: effect.abilityId,
        rank: cumulativeRank(effect.baseRank, ctx.count),
        isPrimary: effect.isPrimary,
        movesUsedThisCamp: [],
        movesUsedThisSession: [],
      });
      return;
    case 'skill-rank-from-narrative': {
      const abilityId = readAbilityIdFromNarrative(ctx, effect.narrativeFieldId);
      acc.skillAdditions.push({
        abilityId,
        rank: cumulativeRank(effect.baseRank, ctx.count),
        isPrimary: effect.isPrimary,
        movesUsedThisCamp: [],
        movesUsedThisSession: [],
      });
      return;
    }
    case 'modifier-from-narrative': {
      const statKey = readStatIdFromNarrative(ctx, effect.narrativeFieldId);
      acc.modifiers[statKey] = effect.baseAmount + (ctx.count - 1);
      return;
    }
    case 'narrative-trait':
      acc.traitAdditions.push({
        id: effect.traitId,
        label: effect.label,
        ...(effect.description ? { description: effect.description } : {}),
      });
      return;
    case 'custom-trait-from-narrative':
      acc.traitAdditions.push(...buildCustomTraitInstances(effect, ctx));
      return;
    case 'hp-bonus-scaling':
      // **D8a**: 1 sola selezione applica il bonus (manuale non prevede
      // cumulativa per "resistente" né su Druido né su Sciamano). Se
      // l'utente seleziona la stessa peculiarità 2× per errore, la 2ª
      // sovrascrive con valori identici — semantica idempotente.
      acc.hpBonusScaling = {
        perCharacterLevel: effect.perCharacterLevel,
        max: effect.max,
      };
      return;
    case 'cost-suspension':
      acc.costSuspended = true;
      return;
  }
}

function cumulativeRank(baseRank: 1 | 2, count: number): 1 | 2 | 3 {
  const rank = baseRank + Math.max(0, count - 1);
  return Math.min(3, rank) as 1 | 2 | 3;
}

function readAbilityIdFromNarrative(
  ctx: PeculiarityDispatchContext,
  fieldId: string,
): FollowerAbilityId {
  const raw = readField(ctx.input.narrativeFieldValues, fieldId);
  if (raw.length === 0) {
    throw new Error(
      `Template '${ctx.template.id}': peculiarità '${ctx.optionId}' richiede NarrativeField '${fieldId}' compilato (Abilità)`,
    );
  }
  if (!FOLLOWER_ABILITY_IDS.includes(raw as FollowerAbilityId)) {
    throw new Error(
      `Template '${ctx.template.id}': peculiarità '${ctx.optionId}' richiede una Abilità valida; ricevuto '${raw}'`,
    );
  }
  return raw as FollowerAbilityId;
}

function readStatIdFromNarrative(
  ctx: PeculiarityDispatchContext,
  fieldId: string,
): keyof FollowerModifiers {
  const raw = readField(ctx.input.narrativeFieldValues, fieldId);
  if (raw.length === 0) {
    throw new Error(
      `Template '${ctx.template.id}': peculiarità '${ctx.optionId}' richiede NarrativeField '${fieldId}' compilato (stat)`,
    );
  }
  const statKey = STAT_IDS_TO_MOD_KEY[raw];
  if (statKey === undefined) {
    throw new Error(
      `Template '${ctx.template.id}': peculiarità '${ctx.optionId}' richiede uno StatId valido; ricevuto '${raw}'`,
    );
  }
  return statKey;
}

function buildCustomTraitInstances(
  effect: Extract<PeculiarityEffect, { kind: 'custom-trait-from-narrative' }>,
  ctx: PeculiarityDispatchContext,
): FollowerTrait[] {
  const traits: FollowerTrait[] = [];
  traits.push(
    readCustomTrait(ctx, {
      labelFieldId: effect.labelFieldId,
      descriptionFieldId: effect.descriptionFieldId,
      occurrenceIndex: 1,
    }),
  );
  if (ctx.count >= 2) {
    if (!effect.labelFieldIdSecond) {
      throw new Error(
        `Template '${ctx.template.id}': peculiarità '${ctx.optionId}' selezionata 2× ma 'labelFieldIdSecond' non definito sull'effetto`,
      );
    }
    traits.push(
      readCustomTrait(ctx, {
        labelFieldId: effect.labelFieldIdSecond,
        descriptionFieldId: effect.descriptionFieldIdSecond,
        occurrenceIndex: 2,
      }),
    );
  }
  return traits;
}

interface CustomTraitFieldSpec {
  labelFieldId: string;
  descriptionFieldId: string | undefined;
  occurrenceIndex: 1 | 2;
}

function readCustomTrait(
  ctx: PeculiarityDispatchContext,
  spec: CustomTraitFieldSpec,
): FollowerTrait {
  const label = readField(ctx.input.narrativeFieldValues, spec.labelFieldId);
  if (label.length === 0) {
    throw new Error(
      `Template '${ctx.template.id}': peculiarità '${ctx.optionId}' (occorrenza ${spec.occurrenceIndex}) richiede NarrativeField '${spec.labelFieldId}' compilato (label custom)`,
    );
  }
  const description = spec.descriptionFieldId
    ? readField(ctx.input.narrativeFieldValues, spec.descriptionFieldId)
    : '';
  return {
    id: `custom-${ctx.optionId}-${spec.occurrenceIndex}`,
    label,
    ...(description.length > 0 ? { description } : {}),
  };
}

function readField(values: Readonly<Record<string, string>>, fieldId: string): string {
  return values[fieldId]?.trim() ?? '';
}

function isFieldFilled(values: Readonly<Record<string, string>>, fieldId: string): boolean {
  return (values[fieldId]?.trim().length ?? 0) > 0;
}
